import React from 'react';
import { ArrowRight, Bot, KeyRound, Receipt, ShoppingBag, Wallet } from 'lucide-react';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import BrandLogo from '@/components/shared/BrandLogo';
import Footer from '@/components/shared/Footer';

const MCP_NPM = 'https://www.npmjs.com/package/@vaiu/mcp-server';
const MCP_PACKAGE = '@vaiu/mcp-server';
const VAIU_APP = 'https://vaiu.app';
const DEVELOPER_APP = 'https://developer.vaiu.io';
const API_BASE = 'https://api.vaiu.io/api/v1';

const mcpConfigJson = `{
  "mcpServers": {
    "vaiu": {
      "command": "npx",
      "args": ["-y", "${MCP_PACKAGE}"],
      "env": {
        "VAIU_USERNAME": "<bot email or username>",
        "VAIU_PASSWORD": "<bot password>",
        "VAIU_CLIENT_KEY": "<app_… API key from developer.vaiu.io>",
        "VAIU_API_BASE": "https://api.vaiu.io"
      }
    }
  }
}`;

const registerCurl = `curl -sS -X POST ${API_BASE}/register \\
  -H "Content-Type: application/json" \\
  -H "client-key: app_<your-developer-app-key>" \\
  -d '{"username":"my-bot","email":"bot@example.com","password":"<secret>"}'`;

const loginCurl = `curl -sS -X POST ${API_BASE}/login \\
  -H "Content-Type: application/json" \\
  -H "client-key: app_<your-developer-app-key>" \\
  -d '{"username":"my-bot","password":"<secret>"}'`;

const createAppCurl = `curl -sS -X POST ${API_BASE}/developer/apps \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <jwt-from-login>" \\
  -H "client-key: app_<existing-key>" \\
  -d '{"name":"MCP agent","description":"Pays and invoices on Vaiu"}'`;

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-950 p-4 text-xs leading-relaxed text-slate-100">
      <code>{children}</code>
    </pre>
  );
}

/**
 * Dedicated home for bots.vaiu.app — MCP setup, registration, API keys.
 */
const BotsLandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <BrandLogo size="sm" variant="default" className="max-h-8" />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={VAIU_APP}>vaiu.app</a>
            </Button>
            <Button size="sm" asChild>
              <a href={DEVELOPER_APP}>
                Developer console
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-10 px-4 py-10 sm:px-6 pb-16">
        <section className="space-y-4 text-center">
          <Badge variant="secondary" className="uppercase tracking-wide">
            For humans first
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Vaiu for bots
          </h1>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
            Vaiu lets software agents move money the same way people do: unique cash and invoice
            tokens, not shared card numbers. A bot can <strong>pay another bot</strong> by minting a
            cash token, <strong>ask to get paid</strong> by issuing an invoice token, and{' '}
            <strong>run commerce on behalf of a human</strong>—checkout, balances, and settlement—
            once it is registered on the platform and connected through MCP.
          </p>
          <p className="mx-auto max-w-2xl text-sm text-gray-500">
            This host is a setup guide for agents and the people who operate them. Everyday
            payments for people stay on{' '}
            <a className="font-medium text-slate-800 underline" href={VAIU_APP}>
              vaiu.app
            </a>
            .
          </p>
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          {[
            {
              icon: Wallet,
              title: 'Pay other bots',
              body: 'Create a cash token for an amount and currency, share the six-character code, and the other agent redeems it into its balance.',
            },
            {
              icon: Receipt,
              title: 'Ask to get paid',
              body: 'Create an invoice token. Another bot (or a human) pays that invoice with a cash token or from a Vaiu balance.',
            },
            {
              icon: ShoppingBag,
              title: 'Commerce for humans',
              body: 'Act as the human’s agent: hold funds, pay merchants, and settle services without exposing the human’s card details to the counterparty.',
            },
          ].map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border-gray-200/80 shadow-sm">
              <CardContent className="space-y-3 pt-6">
                <div className="inline-flex rounded-lg bg-slate-100 p-2.5">
                  <Icon className="h-5 w-5 text-slate-800" />
                </div>
                <h2 className="font-semibold text-gray-900">{title}</h2>
                <p className="text-sm leading-relaxed text-gray-600">{body}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="space-y-4" id="mcp">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-slate-800" />
            <h2 className="text-2xl font-bold text-gray-900">1. Connect Vaiu MCP from npm</h2>
          </div>
          <p className="text-gray-600 leading-relaxed">
            Agents should talk to Vaiu through the published MCP server, not by calling mint or
            redeem HTTP routes themselves. Package:{' '}
            <a className="font-medium text-slate-900 underline" href={MCP_NPM}>
              {MCP_PACKAGE}
            </a>{' '}
            on npmjs.com (this is the Vaiu MCP server; install with that scoped name).
          </p>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
            <li>
              Install or run via npx. Confirm tools such as <code>create_cash_token</code>,{' '}
              <code>redeem_token</code>, <code>get_balance</code>, <code>create_invoice_token</code>.
            </li>
            <li>
              Merge this into existing MCP config. Cursor: <code>~/.cursor/mcp.json</code>. Claude
              Desktop:{' '}
              <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>. Do not
              wipe other servers.
            </li>
            <li>Reload MCP (Cursor Settings → MCP, or restart Claude Desktop).</li>
            <li>
              Smoke check: <code>get_balance</code> for <code>USD</code>. A zero balance still means
              the connection works.
            </li>
          </ol>
          <CodeBlock>{mcpConfigJson}</CodeBlock>
          <p className="text-sm text-gray-600">
            Use a <strong>buyer / bot user</strong> account, not a sell.vaiu.app POS key.{' '}
            <code>VAIU_CLIENT_KEY</code> is the developer-app API key that starts with{' '}
            <code>app_</code>.
          </p>
        </section>

        <section className="space-y-4" id="register">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-slate-800" />
            <h2 className="text-2xl font-bold text-gray-900">2. Register the bot and get an API key</h2>
          </div>
          <p className="text-gray-600 leading-relaxed">
            Every bot is a Vaiu user. The API key is a <strong>developer app</strong> client-key
            (<code>app_…</code>). You typically create that app once in the developer console, then
            the bot can register or log in over the API using that key.
          </p>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Option A — vaiu.app UX (recommended first time)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-gray-700">
              <ol className="list-decimal space-y-2 pl-5">
                <li>
                  Create a person/buyer account on{' '}
                  <a className="font-medium underline" href={`${VAIU_APP}/signup`}>
                    vaiu.app/signup
                  </a>{' '}
                  (or sign in at{' '}
                  <a className="font-medium underline" href={`${VAIU_APP}/login`}>
                    /login
                  </a>
                  ). This can be the operator; you can add a dedicated bot username later via API.
                </li>
                <li>
                  Open{' '}
                  <a className="font-medium underline" href={DEVELOPER_APP}>
                    developer.vaiu.io
                  </a>
                  , create an app (for example “MCP agent”), and copy the API key. That value is{' '}
                  <code>VAIU_CLIENT_KEY</code> / the <code>client-key</code> header.
                </li>
                <li>
                  Put username, password, and that key into MCP config as above. Reload MCP.
                </li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Option B — platform API</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-700">
              <p>
                All writes need header <code>client-key</code> (the <code>app_…</code> key) and{' '}
                <code>Content-Type: application/json</code>. Login and token routes also use{' '}
                <code>Authorization: Bearer &lt;jwt&gt;</code> after login. Base URL:{' '}
                <code>{API_BASE}</code>.
              </p>
              <p className="font-medium text-gray-900">Register</p>
              <CodeBlock>{registerCurl}</CodeBlock>
              <p className="font-medium text-gray-900">Login (returns JWT)</p>
              <CodeBlock>{loginCurl}</CodeBlock>
              <p className="font-medium text-gray-900">Create a developer app (API key)</p>
              <p>
                A user can own one developer app. If you already have a key from Option A, you do
                not need this call. Response includes <code>apiKey</code> (the client-key).
              </p>
              <CodeBlock>{createAppCurl}</CodeBlock>
              <p>
                List apps: <code>GET {API_BASE}/developer/apps</code> with the same headers. Rotate:{' '}
                <code>POST {API_BASE}/developer/apps/&lt;appId&gt;/api-key</code>.
              </p>
            </CardContent>
          </Card>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-gray-600">
          <p className="font-medium text-gray-900">What not to do</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Do not put merchant POS keys from sell.vaiu.app into MCP.</li>
            <li>Do not commit passwords or API keys to git.</li>
            <li>
              Do not mint or redeem by calling <code>api.vaiu.io</code> from the agent when MCP
              tools are available—use the MCP server.
            </li>
          </ul>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BotsLandingPage;
